import { Property } from '../types/property';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { Bed, Bath, Car, Square, MapPin, Heart, Phone, Mail } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';

interface PropertyCardProps {
  property: Property;
  onClick?: () => void;
  isFavorite?: boolean;
  onToggleFavorite?: (propertyId: string) => void;
}

// Status color mapping per design spec v2.1
const STATUS_COLORS: Record<string, string> = {
  Active: 'bg-[#10B981] text-white',
  Sold: 'bg-[#EF4444] text-white',
  Pending: 'bg-[#F59E0B] text-black',
  Withdrawn: 'bg-[#6B7280] text-white',
  Rented: 'bg-blue-500/90 text-white',
  Cancelled: 'bg-[#6B7280] text-white',
  Expired: 'bg-gray-600/90 text-white',
};

// v2.1 spec: format price as R X,XXX,XXX
function formatPrice(price: number): string {
  if (!price && price !== 0) return 'Price on request';
  return `R ${price.toLocaleString('en-ZA')}`;
}

// Display labels for property types (v2.1 spec)
const TYPE_LABELS: Record<string, string> = {
  House: 'House',
  Apartment: 'Apartment',
  Townhouse: 'Townhouse',
  Land: 'Vacant Land',
  Farm: 'Farm',
  VacantLand: 'Vacant Land',
  Commercial: 'Commercial',
};

export function PropertyCard({ property, onClick, isFavorite, onToggleFavorite }: PropertyCardProps) {
  const coverImage = property.media?.find(m => m.isCover) || property.media?.[0];

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Derive display location
  const locationDisplay = [property.address.suburb, property.address.city]
    .filter(Boolean)
    .join(', ');

  const statusKey = property.listingStatus || 'Active';
  const statusClass = STATUS_COLORS[statusKey] || STATUS_COLORS.Active;

  return (
    <Card
      data-testid="property-card"
      className="group cursor-pointer overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/50"
      onClick={onClick}
    >
      <div className="relative aspect-[16/9] overflow-hidden">
        {coverImage ? (
          <>
            <ImageWithFallback
              src={coverImage.url}
              alt={coverImage.alt}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          </>
        ) : (
          <div className="w-full h-full bg-muted flex items-center justify-center">
            <Square className="w-12 h-12 text-muted-foreground/30" />
          </div>
        )}
        
        {/* Price badge — large and prominent (spec: 24px bold) */}
        <div className="absolute bottom-3 left-3">
          <span data-testid="property-card-price" className="text-lg font-bold text-white drop-shadow-lg">
            {formatPrice(property.price)}
          </span>
        </div>

        {/* Favorite button */}
        {onToggleFavorite && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite(property.id);
            }}
            className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center hover:bg-black/60 transition-colors z-10"
            aria-label={isFavorite ? 'Remove from saved' : 'Save property'}
          >
            <Heart
              className={`w-4.5 h-4.5 transition-colors ${
                isFavorite ? 'fill-red-500 text-red-500' : 'text-white'
              }`}
            />
          </button>
        )}

        {/* Status & feature badges — top-right stack */}
        <div className="absolute top-3 right-3 flex flex-col items-end gap-1">
          <Badge className={`${statusClass} backdrop-blur-sm border-0 shadow-lg text-xs`}>
            {statusKey}
          </Badge>
          {property.noTransferDuty && (
            <Badge className="bg-emerald-600/90 backdrop-blur-sm text-white border-0 shadow-lg text-xs">
              No Transfer Duty
            </Badge>
          )}
          {property.priceReduced && (
            <Badge className="bg-red-600/90 backdrop-blur-sm text-white border-0 shadow-lg text-xs">
              Price Reduced
            </Badge>
          )}
          {property.flags.new && (
            <Badge className="bg-blue-600/90 backdrop-blur-sm text-white border-0 shadow-lg text-xs">
              New
            </Badge>
          )}
          {property.flags.featured && (
            <Badge className="bg-primary/90 backdrop-blur-sm text-primary-foreground border-0 shadow-lg text-xs">
              Featured
            </Badge>
          )}
        </div>

        {/* Property type & mandate — top-left */}
        <div className="absolute top-3 left-3 flex items-center gap-1.5">
          <Badge variant="secondary" className="bg-background/90 backdrop-blur-sm text-foreground border-0 shadow-lg text-xs">
            {TYPE_LABELS[property.type] || property.type}
          </Badge>
          {property.mandateType && (
            <Badge variant="secondary" className="bg-background/90 backdrop-blur-sm text-foreground border-0 shadow-lg text-xs">
              For {property.mandateType}
            </Badge>
          )}
        </div>
      </div>

      <CardContent className="p-4">
        <div className="space-y-2">
          {/* Title — spec: 16px medium */}
          <h3 data-testid="property-card-title" className="font-semibold line-clamp-2 text-sm leading-snug group-hover:text-primary transition-colors">
            {property.title}
          </h3>

          {locationDisplay && (
            <p data-testid="property-card-location" className="text-muted-foreground text-xs flex items-center gap-1">
              <MapPin className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{locationDisplay}</span>
            </p>
          )}

          {/* Property facts (spec: icon + count) */}
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            {property.facts.bedrooms != null && property.facts.bedrooms > 0 && (
              <div data-testid="property-card-bedrooms" className="flex items-center gap-1">
                <Bed className="w-3.5 h-3.5 text-primary/70" />
                <span className="text-xs">{property.facts.bedrooms}</span>
              </div>
            )}
            {property.facts.bathrooms != null && property.facts.bathrooms > 0 && (
              <div data-testid="property-card-bathrooms" className="flex items-center gap-1">
                <Bath className="w-3.5 h-3.5 text-primary/70" />
                <span className="text-xs">{property.facts.bathrooms}</span>
              </div>
            )}
            {property.facts.parking != null && property.facts.parking > 0 && (
              <div data-testid="property-card-parking" className="flex items-center gap-1">
                <Car className="w-3.5 h-3.5 text-primary/70" />
                <span className="text-xs">{property.facts.parking}</span>
              </div>
            )}
            {property.facts.floorSize != null && property.facts.floorSize > 0 && (
              <div data-testid="property-card-floor-size" className="flex items-center gap-1">
                <Square className="w-3.5 h-3.5 text-primary/70" />
                <span className="text-xs">{property.facts.floorSize} m²</span>
              </div>
            )}
            {property.facts.erfSize != null && property.facts.erfSize > 0 && !property.facts.floorSize && (
              <div data-testid="property-card-floor-size" className="flex items-center gap-1">
                <Square className="w-3.5 h-3.5 text-primary/70" />
                <span className="text-xs">{property.facts.erfSize} m²</span>
              </div>
            )}
          </div>

          {/* Agent info and listing date */}
          <div className="pt-2 border-t border-border/50 space-y-2">
            <div className="flex justify-between items-center">
              {property.agent?.name ? (
                <div className="flex items-center gap-2">
                  <Avatar className="w-7 h-7 border border-border/50 flex-shrink-0">
                    {property.agent.avatarUrl && (
                      <AvatarImage src={property.agent.avatarUrl} alt={property.agent.name} />
                    )}
                    <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                      {getInitials(property.agent.name)}
                    </AvatarFallback>
                  </Avatar>
                  <span data-testid="property-card-agent-name" className="text-xs font-medium text-foreground truncate max-w-[120px]">
                    {property.agent.name}
                  </span>
                </div>
              ) : (
                <Badge variant="outline" className="text-xs border-border/50">
                  {TYPE_LABELS[property.type] || property.type}
                </Badge>
              )}
              <span className="text-xs text-muted-foreground flex-shrink-0">
                {new Date(property.listingDate).toLocaleDateString('en-ZA', {
                  month: 'short',
                  day: 'numeric'
                })}
              </span>
            </div>

            {/* Agent contact details — always visible, no auth required */}
            {(property.agent?.phone || property.agent?.email) && (
              <div className="flex flex-wrap gap-x-3 gap-y-1">
                {property.agent.phone && (
                  <a
                    href={`tel:${property.agent.phone}`}
                    onClick={e => e.stopPropagation()}
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
                  >
                    <Phone className="w-3 h-3" />
                    <span>{property.agent.phone}</span>
                  </a>
                )}
                {property.agent.email && (
                  <a
                    href={`mailto:${property.agent.email}`}
                    onClick={e => e.stopPropagation()}
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
                  >
                    <Mail className="w-3 h-3" />
                    <span className="truncate max-w-[140px]">{property.agent.email}</span>
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
